import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { useCompanyConfigContext } from './CompanyConfigContext';
import { getBrandFromHostname } from '../constants/brands';

interface ThemeContextType {
  mainColor: string;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface CompanyThemeProviderProps {
  children: ReactNode;
}

export const CompanyThemeProvider: React.FC<CompanyThemeProviderProps> = ({ children }) => {
  const { loading, configs } = useCompanyConfigContext();
  const brand = getBrandFromHostname(window.location.hostname);

  const mainColor = useMemo(() => {
    if (!loading && Object.keys(configs).length > 0 && configs['css.main.color']) {
      const color = configs['css.main.color'];
      return typeof color === 'string' && color.startsWith('#') ? color : `#${color}`;
    }
    return brand.primaryColor;
  }, [loading, configs, brand.primaryColor]);

  const theme = useMemo(() => {
    return createTheme({
      palette: {
        primary: {
          main: mainColor,
          light: `${mainColor}15`,
          dark: mainColor,
        },
        secondary: {
          main: '#74788d',
        },
      },
      typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
            },
            contained: {
              backgroundColor: mainColor,
              '&:hover': {
                backgroundColor: mainColor,
                filter: 'brightness(0.9)',
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            colorPrimary: {
              backgroundColor: mainColor,
              color: '#fff',
            },
          },
        },
      },
    });
  }, [mainColor]);

  const contextValue = useMemo(() => ({
    mainColor,
    theme,
  }), [mainColor, theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useCompanyTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCompanyTheme must be used within a CompanyThemeProvider');
  }
  return context;
};
